import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import { BrowserRouter, Routes, Route } from 'react-router';
import { UMModals } from '@fanduel/cpe-um-bundle/core-modal-presenter/library';
import { bootstrapEmbeddedOrFallbackToPopup } from '@/logic/embedded-availability';
import { isDetachedMode } from '@/logic/embedded-mode';
import { ThemeProvider } from '@/theme/ThemeProvider.tsx';
import { QueryProvider } from '@/query/QueryProvider';
import { bootstrapPersistedQueryClient } from '@/query/bootstrapPersistedQueryClient';
import { hydrateCookieSession } from '@/unified-modules/account/annexes/cookie-session';
import TestUI from '@/routes/TestUi/TestUI.tsx';
import { resolveRouterBasename } from '@/utilities/helpers/same-origin-path';
import DownloadPlugin from './routes/Plugin/DownloadPlugin.tsx';
import './styles/index.scss';

if (process.env.NODE_CONFIG_ENV !== 'prod' && process.env.NODE_ENV !== 'test') {
  void import('@/dev/reality-check-dev-trigger');
}

// Called for its side effect (popup fallback when the embed shell is unusable).
bootstrapEmbeddedOrFallbackToPopup();

// Optimistically hydrate launch-critical session state from lobby cookies so a
// logged-in shell can render before Account UM reconciles the session.
//
// Stale-session cleanup no longer runs here. Clearing at boot deleted the auth
// token before Account UM could resolve the session — and because the optimistic
// hydrate requires a Session-ID cookie that the lobby (casino-next) never sets,
// it always failed and always cleared, dropping standalone/detached launches to
// the login page. The clear now runs in initializeAccountUM, after init, and
// only when no session resolves and the session cookie is genuinely absent.
if (!isDetachedMode()) {
  hydrateCookieSession();
}

// Public mount paths are an HTTP routing concern, independent of Vite's asset base.
const routerBasename = resolveRouterBasename(globalThis.location.pathname);

void bootstrapPersistedQueryClient().then(({ queryClient, persister }) => {
  createRoot(document.getElementById('game-launcher-wrapper')!).render(
    <StrictMode>
      <ThemeProvider>
        <QueryProvider queryClient={queryClient} persister={persister}>
          <BrowserRouter basename={routerBasename}>
            <Routes>
              <Route path="/" element={<App />} />
              <Route path="/:gameUid" element={<App />} />
              <Route path="/plc-plugin" element={<DownloadPlugin />} />

              {/* Test routes - only available in development environment */}
              {process.env.NODE_ENV === 'development' && (
                <>
                  <Route path="/test-ui" element={<TestUI />} />
                </>
              )}
            </Routes>
            <UMModals />
          </BrowserRouter>
        </QueryProvider>
      </ThemeProvider>
    </StrictMode>,
  );
});
